import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardMetadata } from '../../hooks/useDashboardMetadata';
import { useTenders } from '../../hooks/useTenders';
import type { Tender } from '../../types/tender';
import { filterVisibleTenders } from '../../utils/dashboardUtils';
import ArchiveDetailModal from './ArchiveDetailModal';
import ArchiveTable from './ArchiveTable';

const ArchivePage: React.FC = () => {
  const { isLoading: isLoadingAuth } = useAuth();
  const { account } = useAccount();
  const theme = useTheme();

  const { usersMap } = useDashboardMetadata(account);
  const { tenders: rawTenders, isLoadingTenders, tendersError } = useTenders({ archived: true });

  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const tenders = useMemo(() => {
    const sorted = [...rawTenders];
    sorted.sort((a, b) => {
      const dateA = a.archivedDate || a.updatedAt || 0;
      const dateB = b.archivedDate || b.updatedAt || 0;
      const timeA =
        dateA && typeof dateA === 'object' && 'toMillis' in dateA
          ? (dateA as { toMillis: () => number }).toMillis()
          : new Date(dateA as string | Date).getTime();
      const timeB =
        dateB && typeof dateB === 'object' && 'toMillis' in dateB
          ? (dateB as { toMillis: () => number }).toMillis()
          : new Date(dateB as string | Date).getTime();
      return timeB - timeA;
    });
    return sorted;
  }, [rawTenders]);

  // Filter tenders for Team Leads (re-using the dashboard utility if applicable)
  const visibleTenders = useMemo(
    () => filterVisibleTenders(tenders, account, usersMap),
    [tenders, account, usersMap]
  );

  const uniqueIssuers = useMemo(() => {
    const issuers = new Set<string>();
    for (const tender of tenders) {
      if (tender.tenderIssuer) {
        issuers.add(tender.tenderIssuer);
      }
    }
    return Array.from(issuers).sort();
  }, [tenders]);

  const handleRowClick = (tender: Tender) => {
    setSelectedTender(tender);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTender(null);
  };

  if (isLoadingAuth || !account) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <CircularProgress
          sx={{ color: theme.palette.bidlogiq?.orange || '#FFA500' }}
          size={60}
          thickness={4}
        />
        <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.primary }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <PageContainer title="Archived Tenders" maxWidth={false}>
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, pb: 4, pt: 2 }}>
        {tendersError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {tendersError}
          </Typography>
        )}
        <ArchiveTable
          tenders={visibleTenders}
          isLoading={isLoadingTenders}
          usersMap={usersMap}
          onRowClick={handleRowClick}
        />
        <ArchiveDetailModal
          open={isModalOpen}
          onClose={handleCloseModal}
          tender={selectedTender}
          usersMap={usersMap}
          uniqueIssuers={uniqueIssuers}
        />
      </Box>
    </PageContainer>
  );
};

export default ArchivePage;
